import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ContextMenu.module.css';

export type MenuItem =
  | { label: string; onClick?: () => void; danger?: boolean; disabled?: boolean; hint?: string; children?: MenuItem[] }
  | { separator: true }
  | { heading: string };

function Items({ items, onDone, flip }: { items: MenuItem[]; onDone: () => void; flip: boolean }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <>
      {items.map((item, i) => {
        if ('separator' in item) return <div key={i} className={styles.sep} />;
        if ('heading' in item) return <div key={i} className={styles.heading}>{item.heading}</div>;
        const has = (item.children?.length ?? 0) > 0;
        return (
          <div key={i} style={{ position: 'relative' }} onPointerEnter={() => setOpen(has ? i : null)}>
            <button
              type="button"
              className={`${styles.item} ${item.danger ? styles.danger : ''}`}
              disabled={item.disabled}
              onClick={() => {
                if (has) return setOpen(i);
                item.onClick?.();
                onDone();
              }}
            >
              <span>{item.label}</span>
              {has ? <span aria-hidden="true">›</span> : item.hint ? <span className={styles.hint}>{item.hint}</span> : null}
            </button>
            {has && open === i && (
              <div className={`${styles.sub} ${flip ? styles.subLeft : ''}`} role="menu">
                <Items items={item.children!} onDone={onDone} flip={flip} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/** Kontextmenü an einer Bildschirmposition. Schließt bei Klick daneben, Escape oder Scrollen. */
export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: MenuItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  const [flip, setFlip] = useState(false);

  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ x: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), y: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
    setFlip(x + r.width + 280 > window.innerWidth);
  }, [x, y]);

  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('blur', onClose);
    window.addEventListener('wheel', onClose, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('blur', onClose);
      window.removeEventListener('wheel', onClose);
    };
  }, [onClose]);

  return (
    <div ref={ref} className={styles.menu} style={{ left: pos.x, top: pos.y }} role="menu" onContextMenu={(e) => e.preventDefault()}>
      <Items items={items} onDone={onClose} flip={flip} />
    </div>
  );
}
